/**
 * i18n bootstrap for the Tyndo shell.
 *
 * THREE KINDS OF LOCALE, AND WHY THE DISTINCTION IS LOAD-BEARING
 * ─────────────────────────────────────────────────────────────
 *   SHIPPED    a language with a genuinely written resource bundle behind it.
 *              `en-US` (source) and `de-DE`, written for the shell chrome, the
 *              batch-record workflow, and every module string that has been
 *              routed through the catalogue so far. The vast majority of module
 *              prose is still hardcoded English in the JSX — that inventory is
 *              what scripts/audit-operator-string-localization.mjs ratchets
 *              down, and a `de-DE` bundle is not a claim that the module is
 *              fully translated.
 *   PSEUDO     `en-XA` / `ar-XB` — machine-transformed English, generated by
 *              scripts/generate-pseudo-locale.mjs. They exist to make
 *              untranslated strings, layout expansion and RTL breakage VISIBLE.
 *              They are selectable so a developer or QA reviewer can switch
 *              into them, and they are excluded from {@link SHIPPED_LOCALES} so
 *              they can never be offered to a customer as a language.
 *   NEITHER    anything else. A locale with no bundle is not advertised. The
 *              previous six-locale switcher was removed precisely because it
 *              advertised languages with no translated views behind them — the
 *              claim was the switcher, not the substrate.
 *
 * `XA` and `XB` are Unicode CLDR user-assigned region subtags: they can never
 * denote a real country, which is why they are the conventional pseudo-locale
 * tags. Nothing here may move one of them into SHIPPED_LOCALES.
 *
 * Resources are loaded from `/locales/{lng}/{ns}.json` (served from
 * `apps/shell/public/locales/`). The init call uses `react.useSuspense: false`
 * so the initial render is never blocked on a slow locale fetch.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';

/** Locales with a genuinely written bundle. These may be offered to a user. */
export const SHIPPED_LOCALES = ['en-US', 'de-DE'] as const;

/**
 * Generated pseudo-locales. Selectable for verification, NEVER presented as a
 * language the product supports.
 */
export const PSEUDO_LOCALES = ['en-XA', 'ar-XB'] as const;

/** Everything i18next will load a bundle for. */
export const SUPPORTED_LOCALES = [...SHIPPED_LOCALES, ...PSEUDO_LOCALES] as const;

export type ShippedLocale = (typeof SHIPPED_LOCALES)[number];
export type PseudoLocale = (typeof PSEUDO_LOCALES)[number];
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/** True for a generated pseudo-locale. */
export function isPseudoLocale(tag: string): boolean {
  return (PSEUDO_LOCALES as readonly string[]).includes(tag);
}

/**
 * Writing direction per locale. `ar-XB` is the ONLY right-to-left entry, and it
 * is a pseudo-locale: the shell's direction handling, icon mirroring and input
 * alignment are exercised without shipping an Arabic bundle and without
 * claiming Arabic support.
 */
const DIRECTION: Record<string, 'ltr' | 'rtl'> = {
  'en-US': 'ltr',
  'de-DE': 'ltr',
  'en-XA': 'ltr',
  'ar-XB': 'rtl',
};

/** `'ltr'` unless the locale is explicitly right-to-left. */
export function directionOf(tag: string): 'ltr' | 'rtl' {
  return DIRECTION[tag] ?? 'ltr';
}

/**
 * Render a missing key VISIBLY rather than as the key path.
 *
 * i18next's default is to render the key itself, which in practice reads like a
 * plausible (if terse) label — `records.title` on a page header looks like a
 * design choice, not a defect. Wrapping it makes a gap in the catalogue
 * unmistakable in a screenshot, in a Playwright assertion, and to a reviewer
 * paging through the pseudo-locale.
 */
export function markMissingKey(key: string): string {
  return `⟦${key}⟧`;
}

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en-US',
    supportedLngs: [...SUPPORTED_LOCALES],
    load: 'currentOnly',
    // One namespace per module bundle, plus the shell's `common`. A module view
    // calls `useTranslation('<its namespace>')`; listing them here PRELOADS the
    // bundle so the first render of a federated view never flashes a marked
    // missing key while HttpBackend fetches on demand.
    ns: [
      'common',
      'admin',
      'agents',
      'alarms',
      'batch',
      'controls',
      'inventory',
      'maintenance',
      'materials',
      'plm',
      'sales',
    ],
    defaultNS: 'common',
    // A missing key renders MARKED rather than as the bare key path, so a gap
    // in the catalogue is unmistakable in a screenshot and assertable in a
    // test. This matters most under a pseudo-locale, where a key that silently
    // rendered its English source would defeat the whole mechanism.
    parseMissingKeyHandler: (key: string) => markMissingKey(key),
    interpolation: {
      escapeValue: false, // React already escapes
    },
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'tyndo.locale',
    },
    react: {
      // CRITICAL: do not suspend the initial render on slow locale loads.
      // The shell prefers a brief flash of the en-US key over a blocked boot.
      useSuspense: false,
    },
  });

/**
 * Keep `<html lang>` and `<html dir>` in step with the active language.
 *
 * Both attributes are accessibility contracts, not cosmetics: a screen reader
 * picks its pronunciation from `lang`, and `dir` is what makes an RTL locale
 * lay out at all. Setting them once at boot is not enough — an in-session
 * language switch must move them too, or the announcement stays in the old
 * language while the visible text changes.
 */
function applyDocumentLocale(tag: string): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('lang', tag);
  document.documentElement.setAttribute('dir', directionOf(tag));
}

applyDocumentLocale(i18n.language || 'en-US');
i18n.on('languageChanged', applyDocumentLocale);

export default i18n;
